import React from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useTheme } from "../lib/theme";
import { Check, Palette, LayoutTemplate, ExternalLink, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { VerdictBadge } from "../components/VerdictBadge";

export default function Themes() {
    const { themeId, setTheme, themes, layoutId, setLayout, layouts } = useTheme();

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
            {/* HEADER */}
            <div className="max-w-2xl mb-14">
                <p className="text-xs uppercase tracking-widest text-primary mb-3">Studio</p>
                <h1 className="font-display text-4xl md:text-6xl text-accent tracking-tight leading-[1.02]">
                    Pick a <span className="text-primary">look</span>.<br />Pick a <span className="text-primary">layout</span>.
                </h1>
                <p className="text-muted-foreground text-lg mt-4 leading-relaxed">
                    Every option is real, live, and applies instantly — your choice is saved on this device.
                    Try each. The site will feel different every time.
                </p>
            </div>

            {/* THEMES */}
            <section className="mb-20">
                <div className="flex items-center gap-3 mb-6">
                    <Palette className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-2xl text-accent">Themes</h2>
                    <span className="text-xs text-muted-foreground ml-2">Currently: <span className="text-accent font-semibold">{themes[themeId].name}</span></span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {Object.values(themes).map((t, i) => (
                        <motion.div
                            key={t.id}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: i * 0.04 }}
                        >
                            <ThemeCard theme={t} active={themeId === t.id} onSelect={() => setTheme(t.id)} />
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* LAYOUTS */}
            <section className="mb-20">
                <div className="flex items-center gap-3 mb-6">
                    <LayoutTemplate className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-2xl text-accent">Homepage layouts</h2>
                    <span className="text-xs text-muted-foreground ml-2">Currently: <span className="text-accent font-semibold">{layouts[layoutId].name}</span></span>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                    {Object.values(layouts).map((l) => (
                        <LayoutCard
                            key={l.id}
                            layout={l}
                            active={layoutId === l.id}
                            onSelect={() => setLayout(l.id)}
                        />
                    ))}
                </div>
            </section>

            {/* PREVIEW OF CURRENT CHOICE */}
            <section>
                <div className="flex items-center gap-3 mb-6">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-2xl text-accent">Live preview</h2>
                </div>
                <div className="card-surface rounded-3xl p-8 md:p-12">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">The claim</div>
                    <h3 className="font-display text-2xl md:text-4xl text-accent leading-tight mb-6">
                        &ldquo;Real-Check can detect scams, phishing, catfishing, and lies in seconds.&rdquo;
                    </h3>
                    <div className="flex flex-wrap gap-3 mb-6">
                        <VerdictBadge verdict="true" confidence={98} size="lg" />
                        <VerdictBadge verdict="safe" confidence={92} size="md" />
                        <VerdictBadge verdict="misleading" confidence={72} size="md" />
                        <VerdictBadge verdict="suspicious" confidence={81} size="md" />
                        <VerdictBadge verdict="scam" confidence={97} size="md" />
                    </div>
                    <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
                        This is how a verdict looks in the <span className="text-accent font-semibold">{themes[themeId].name}</span> theme
                        with the <span className="text-accent font-semibold">{layouts[layoutId].name}</span> layout.
                        Buttons, badges, links, and card surfaces all re-tint automatically.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to="/">
                            <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="themes-open-home-cta">
                                Open the homepage <ExternalLink className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                        <Link to="/about">
                            <Button variant="secondary" className="rounded-full">See how About looks</Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ThemeCard({ theme, active, onSelect }) {
    return (
        <button
            onClick={onSelect}
            data-testid={`theme-card-${theme.id}`}
            className={`text-left w-full rounded-2xl overflow-hidden border transition-all group ${
                active ? "border-primary scale-[1.01] badge-glow-primary" : "border-white/10 hover:border-white/30 hover:-translate-y-0.5"
            }`}
        >
            {/* Preview swatch — uses the theme's OWN colors, not the current theme */}
            <div
                className="relative h-40 p-5 flex items-end"
                style={{
                    background: theme.preview.bg,
                    backgroundImage: theme.heroGlow,
                }}
            >
                <div className="relative z-10">
                    <div className="flex gap-1.5 mb-3">
                        <span className="w-6 h-6 rounded-full" style={{ background: theme.preview.primary, boxShadow: `0 0 20px ${theme.preview.primary}88` }} />
                        <span className="w-6 h-6 rounded-full" style={{ background: theme.preview.accent, opacity: 0.9 }} />
                        <span className="w-6 h-6 rounded-full border" style={{ borderColor: `${theme.preview.accent}44` }} />
                    </div>
                    <div style={{ color: theme.preview.accent, fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: -0.5, lineHeight: 1 }}>
                        Truth,{" "}
                        <span style={{ color: theme.preview.primary }}>verified</span>.
                    </div>
                    <div style={{ color: theme.preview.accent, opacity: 0.55, fontSize: 11, marginTop: 6, letterSpacing: 1, textTransform: "uppercase" }}>
                        {theme.mode} · {theme.vibe.join(" · ")}
                    </div>
                </div>
                {active && (
                    <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-lg">
                        <Check className="h-4 w-4 text-primary-foreground" strokeWidth={3} />
                    </div>
                )}
            </div>
            <div className="p-5">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-display text-lg text-accent">{theme.name}</h3>
                    {active && <span className="text-[10px] uppercase font-bold text-primary tracking-widest">Active</span>}
                </div>
                <p className="text-sm text-muted-foreground leading-snug">{theme.tagline}</p>
            </div>
        </button>
    );
}

function LayoutCard({ layout, active, onSelect }) {
    return (
        <button
            onClick={onSelect}
            data-testid={`layout-card-${layout.id}`}
            className={`text-left w-full rounded-2xl overflow-hidden border transition-all p-6 ${
                active ? "border-primary badge-glow-primary" : "border-white/10 hover:border-white/30 hover:-translate-y-0.5"
            }`}
        >
            {/* Miniature wireframe */}
            <div className="h-32 rounded-lg border border-white/10 bg-white/[0.02] p-3 mb-4 relative overflow-hidden">
                {layout.id === "focused" ? (
                    <FocusedWireframe />
                ) : (
                    <SplitWireframe />
                )}
                {active && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />
                    </div>
                )}
            </div>
            <div className="flex items-center justify-between mb-1">
                <h3 className="font-display text-lg text-accent">{layout.name}</h3>
                {active && <span className="text-[10px] uppercase font-bold text-primary tracking-widest">Active</span>}
            </div>
            <p className="text-sm text-muted-foreground">{layout.tagline}</p>
        </button>
    );
}

function FocusedWireframe() {
    return (
        <div className="flex flex-col items-center justify-center h-full gap-2">
            <div className="w-3/5 h-2 rounded-full bg-white/25" />
            <div className="w-2/5 h-2 rounded-full bg-white/15" />
            <div className="w-4/5 h-6 rounded-md bg-primary/40 mt-2" />
            <div className="flex gap-1 mt-1">
                <div className="w-8 h-1.5 rounded-full bg-white/15" />
                <div className="w-8 h-1.5 rounded-full bg-white/15" />
                <div className="w-8 h-1.5 rounded-full bg-white/15" />
            </div>
        </div>
    );
}

function SplitWireframe() {
    return (
        <div className="grid grid-cols-2 gap-2 h-full">
            <div className="flex flex-col justify-center gap-2">
                <div className="w-full h-1.5 rounded-full bg-white/25" />
                <div className="w-3/4 h-1.5 rounded-full bg-white/15" />
                <div className="w-full h-6 rounded-md bg-primary/40 mt-1" />
            </div>
            <div className="flex flex-col justify-center gap-2 border-l border-white/10 pl-2">
                <div className="w-full h-3 rounded-sm bg-white/15" />
                <div className="w-full h-3 rounded-sm bg-white/10" />
                <div className="w-full h-3 rounded-sm bg-white/10" />
            </div>
        </div>
    );
}
